import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href, e) => {
    e.preventDefault();
    setIsOpen(false);
    
    // If it's a hash link
    if (href.startsWith('#')) {
      if (location.pathname === '/') {
        // Already on home page, just scroll
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to home page first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    setIsOpen(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const handleTransportersClick = (e) => {
    e.preventDefault();
    setIsOpen(false);
    navigate('/drivers');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleCustomerSignInClick = (e) => {
    e.preventDefault();
    setIsOpen(false);
    navigate('/customer/login');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#0a0a0a]/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" onClick={handleHomeClick} className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">H</span>
              </div>
              <span className="text-2xl font-bold text-white">
                Horse<span className="text-amber-500">GO</span>
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <a
              href="/"
              onClick={handleHomeClick}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium"
            >
              {t('nav.home')}
            </a>
            <a
              href="#how-it-works"
              onClick={(e) => handleNavClick('#how-it-works', e)}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium"
            >
              {t('nav.howItWorks')}
            </a>
            <a
              href="#contact"
              onClick={(e) => handleNavClick('#contact', e)}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium"
            >
              {t('nav.contact')}
            </a>
            <a
              href="/customer/login"
              onClick={handleCustomerSignInClick}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium"
              data-testid="navbar-customer-signin"
            >
              {t('nav.forCustomers') || 'For Customers'}
            </a>
            <a
              href="/drivers"
              onClick={handleTransportersClick}
              className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium"
            >
              {t('nav.forTransporters')}
            </a>
          </div>

          {/* Language Selector */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSelector />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-[#0a0a0a]/95 backdrop-blur-md rounded-2xl mt-2 p-4 border border-white/10">
            <div className="flex flex-col space-y-4">
              <a
                href="/"
                className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium py-2"
                onClick={handleHomeClick}
              >
                {t('nav.home')}
              </a>
              <a
                href="#how-it-works"
                className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium py-2"
                onClick={(e) => handleNavClick('#how-it-works', e)}
              >
                {t('nav.howItWorks')}
              </a>
              <a
                href="#contact"
                className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium py-2"
                onClick={(e) => handleNavClick('#contact', e)}
              >
                {t('nav.contact')}
              </a>
              <a
                href="/customer/login"
                className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium py-2"
                onClick={handleCustomerSignInClick}
                data-testid="navbar-mobile-customer-signin"
              >
                {t('nav.forCustomers') || 'For Customers'}
              </a>
              <a
                href="/drivers"
                className="text-gray-300 hover:text-amber-500 transition-colors duration-200 text-sm font-medium py-2"
                onClick={handleTransportersClick}
              >
                {t('nav.forTransporters')}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
